import React from 'react';

import type { CompositionV3DataQuery } from '@revelio/data-access';
import {
  type BreadcrumbSegment,
  getPrimaryColor,
  getPrimaryColorDark,
  TreeMap as ReplotsTreeMap,
  type TreeMapData,
  useChartTheme,
} from '@revelio/replots';

import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import {
  getTreemapInitialLevelOffset,
  isTreemapDrilldownSegment,
  type TreemapDrilldownSegment,
} from '../../filters/utils/treemapDrilldownLevels';
import { usePlotMetric } from '../../hooks/usePlotMetric';
import { convertCompositionDataV3 } from '../DataProvider/composition/composition-v3/convert-composition-data-v3';
import type { NestedTreemapParent } from '../DataProvider/composition/composition-v3/useNestedTreemapParentLookup';
import {
  type DrilldownFilter,
  NestedCompositionTreemapDrilldownProvider,
} from '../DataProvider/composition/NestedCompositionTreemapDrilldownProvider';
import {
  type BreakdownSingleDataSet,
  DataProviderQueryStatus,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../DataProvider/types';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { ChartLoader } from './ChartLoader';
import { useChart } from './ChartProvider';
import { SharableChart } from './chart-menu/plot-share/SharableChart';
import {
  buildNestedTreemapChildren,
  type FlatTreemapItem,
  type NestedTreemapParentTotal,
  nestedTreemapLookupKey,
} from './utils/buildNestedTreemapData';
import { getFormat } from './utils/getFormat';
import { isOtherSegment } from './utils/group-and-filter-data';

// Designer greys for the v1.1 nested treemap "Other" cell. Scoped here so the
// shared/legacy Other colour (getOtherColor) keeps its previous value.
const TREEMAP_OTHER_COLOR = '#84848F';
const TREEMAP_OTHER_COLOR_DARK = '#C8C8CA';

// Root breadcrumb label per drill segment (e.g. geography → "All Geographies"),
// so the "back to root" crumb names what it resets to.
const ALL_LABEL_BY_SEGMENT: Record<TreemapDrilldownSegment, string> = {
  geography: 'All Geographies',
  role: 'All Roles',
  rics: 'All Industries',
  skills: 'All Skills',
};

type DrilldownEntry = {
  level: string;
  id: number | string;
  name: string;
  segment: TreemapDrilldownSegment;
  color?: string;
};

function drilldownEntryToFilter(stack: DrilldownEntry[]): DrilldownFilter {
  const entry = stack[stack.length - 1];
  if (entry === undefined) {
    throw new Error('drilldownEntryToFilter called with empty stack');
  }
  switch (entry.segment) {
    case 'geography':
      return {
        segment: 'geography',
        level: entry.level as 'region' | 'country' | 'state' | 'msa' | 'city',
        id: Number(entry.id),
      };
    case 'role':
      return {
        segment: 'role',
        levelId: entry.level as
          | 'role_k7'
          | 'role_k150'
          | 'role_k1500'
          | 'role_k15000',
        id: String(entry.id),
      };
    case 'rics':
      return {
        segment: 'rics',
        level: entry.level as 'rics_k10' | 'rics_k50' | 'rics_k400' | 'company',
        id: Number(entry.id),
      };
    case 'skills': {
      const nestedId = stack
        .filter((s) => s.segment === 'skills')
        .map((s) => String(s.id))
        .join('_');
      return {
        segment: 'skills',
        level: entry.level as
          | 'skill_0'
          | 'skill_1'
          | 'skill_2'
          | 'skill_3'
          | 'skill_4',
        id: nestedId,
      };
    }
  }
}

const sortOtherLast = <T extends { name: string; id?: string }>(
  items: T[]
): T[] =>
  [...items].sort((a, b) => {
    const aIsOther = isOtherSegment({ dim: a.name, id: a.id });
    const bIsOther = isOtherSegment({ dim: b.name, id: b.id });
    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;
    return 0;
  });

function buildTreemapChildren(
  data: BreakdownSingleDataSet['data'] | SingleDataSet['data']
): TreeMapData['children'] {
  const isSegmented =
    data.length > 0 && data[0] !== undefined && 'breakdown' in data[0];
  const breakdownData = data as BreakdownSingleDataSet['data'];
  const singleEntity =
    breakdownData.length === 1 ? breakdownData[0] : undefined;

  if (singleEntity !== undefined && singleEntity !== null && isSegmented) {
    return sortOtherLast(
      singleEntity.breakdown.map((b) => ({
        name: b.dim,
        value: b.value ?? 0,
        id: b.drilldownId ?? b.id ?? undefined,
        share: b.share,
      }))
    );
  }

  if (isSegmented) {
    return sortOtherLast(
      breakdownData.map((entity) => ({
        name: entity.dim,
        value: entity.breakdown.reduce(
          (sum: number, b: SingleDataPoint) => sum + (b.value ?? 0),
          0
        ),
        children: entity.breakdown.map((b) => ({
          name: b.dim,
          value: b.value ?? 0,
          parentName: entity.dim,
          id: b.drilldownId ?? b.id ?? undefined,
          share: b.share,
        })),
      }))
    );
  }

  return (data as SingleDataSet['data']).map((entity) => ({
    name: entity.dim,
    value: entity.value ?? 0,
  }));
}

function nestFlatChildren(
  children: TreeMapData['children'],
  parentLookup: Map<string, NestedTreemapParent> | null,
  parentTotals?: Map<string, NestedTreemapParentTotal>,
  omitStandaloneOther?: boolean
): TreeMapData['children'] {
  if (parentLookup === null || children.length === 0) return children;

  const alreadyNested = children.some(
    (c) => Array.isArray(c.children) && c.children.length > 0
  );
  if (alreadyNested) return children;

  const flatItems: FlatTreemapItem[] = children.map((c) => ({
    id: String(c.id ?? c.name),
    name: c.name,
    value: c.value ?? 0,
    share: c.share,
  }));
  const nested = buildNestedTreemapChildren(
    flatItems,
    parentLookup,
    parentTotals,
    { omitStandaloneOther }
  );

  const hasGroup = nested.some(
    (node) => Array.isArray(node.children) && node.children.length > 0
  );
  if (!hasGroup) return children;

  return nested;
}

type CompositionV3Payload = NonNullable<
  CompositionV3DataQuery['composition_v3']
>;

type TreemapContentProps = {
  compositionData: CompositionV3Payload;
  monthRefs: string[];
  secondaryDimension: TreemapDrilldownSegment;
  drilldown: DrilldownFilter | null;
  parentLookup: Map<string, NestedTreemapParent> | null;
  parentTotals: Map<string, NestedTreemapParentTotal>;
  metricToUse: MetricValues | undefined;
  format: ReturnType<typeof getFormat>;
  treemapSizeTransform?: 'none' | 'sqrt';
  colorPalette: string[] | undefined;
  colorOverride: { Other: string } | undefined;
  onCellClick?: (
    cellId: string,
    cellName: string,
    parentName?: string,
    color?: string
  ) => void;
  onHeaderClick?: (groupId: string, groupName: string, color?: string) => void;
  breadcrumbPath?: BreadcrumbSegment[];
  onBreadcrumbClick?: (targetDepth: number) => void;
};

function TreemapContent({
  compositionData,
  monthRefs,
  secondaryDimension,
  drilldown,
  parentLookup,
  parentTotals,
  metricToUse,
  format,
  treemapSizeTransform,
  colorPalette,
  colorOverride,
  onCellClick,
  onHeaderClick,
  breadcrumbPath,
  onBreadcrumbClick,
}: TreemapContentProps) {
  const isCompanyEntitiesView =
    secondaryDimension === 'rics' &&
    drilldown?.segment === 'rics' &&
    (drilldown.level === 'rics_k400' || drilldown.level === 'company') &&
    compositionData.entities?.length > 0;

  const treemapChildren = React.useMemo(() => {
    const effectiveMetric = metricToUse ?? 'headcount';
    if (isCompanyEntitiesView) {
      const metricKey =
        effectiveMetric as keyof CompositionV3Payload['entities'][number]['metrics'];
      return compositionData.entities
        .map((entity) => {
          const metricData = entity.metrics?.[metricKey];
          const lastPoint = Array.isArray(metricData)
            ? metricData[metricData.length - 1]
            : undefined;
          const rawValue =
            lastPoint && typeof lastPoint === 'object' && 'val' in lastPoint
              ? (lastPoint as { val?: number }).val
              : undefined;
          const value =
            typeof rawValue === 'number' && Number.isFinite(rawValue)
              ? rawValue
              : 0;
          const name = entity.short_name ?? '';
          if (!name) return null;
          return {
            name,
            value,
            id: entity.id != null ? String(entity.id) : undefined,
          };
        })
        .filter((c): c is NonNullable<typeof c> => c !== null);
    }
    const drilldownDataSet = convertCompositionDataV3({
      metric: effectiveMetric as Parameters<
        typeof convertCompositionDataV3
      >[0]['metric'],
      segment: secondaryDimension,
      data: compositionData,
      monthRefs,
    });

    if (drilldownDataSet.type !== DataType.BREAKDOWN_SINGLE) {
      return null;
    }

    return buildTreemapChildren(
      drilldownDataSet.data as BreakdownSingleDataSet['data']
    ).filter((child) => (child.name ?? '').trim() !== '');
  }, [
    isCompanyEntitiesView,
    compositionData,
    monthRefs,
    metricToUse,
    secondaryDimension,
  ]);

  const nestedChildren = React.useMemo(
    () =>
      treemapChildren === null
        ? null
        : nestFlatChildren(
            treemapChildren,
            parentLookup,
            parentTotals,
            secondaryDimension === 'skills'
          ),
    [treemapChildren, parentLookup, parentTotals, secondaryDimension]
  );

  if (nestedChildren === null) return null;

  const treeData: TreeMapData = { name: 'root', children: nestedChildren };

  return (
    <SharableChart
      Component={ReplotsTreeMap}
      data={treeData}
      format={format}
      sizeTransform={treemapSizeTransform}
      colorPalette={colorPalette}
      colorOverride={colorOverride}
      onCellClick={onCellClick}
      onHeaderClick={onHeaderClick}
      onBreadcrumbClick={onBreadcrumbClick}
      breadcrumbPath={breadcrumbPath}
      horizontal
      enclosed
    />
  );
}

export const NestedTreeMap = ({
  data,
  metric,
}: {
  data: BreakdownSingleDataSet['data'] | SingleDataSet['data'];
  metric?: MetricValues;
}) => {
  const metricFromPlot = usePlotMetric();
  const metricToUse = metric ?? metricFromPlot;
  const { item } = useChart();
  const { primaryFilter } = useTabContentConfig();
  const primaryDimension = primaryFilter?.id;
  const secondaryDimension = item?.plot?.secondaryDimension;
  const { config: pageConfig } = usePageConfig();
  const effectiveFilters = useEffectiveFilters();
  const isReport = pageConfig.type === PageType.REPORT;
  const { theme } = useChartTheme();
  const isDark = theme === 'dark';
  const colorPalette = isReport
    ? [isDark ? getPrimaryColorDark() : getPrimaryColor()]
    : (item?.plot?.colorPalette ?? undefined);
  // The v1.1 nested treemap uses the designer grey for "Other" (light/dark),
  // kept local so the legacy Other colour (getOtherColor) is untouched.
  const colorOverride = isReport
    ? undefined
    : { Other: isDark ? TREEMAP_OTHER_COLOR_DARK : TREEMAP_OTHER_COLOR };

  const format = item?.plot?.format ?? getFormat({ metric: metricToUse });
  const treemapSizeTransform = item?.plot?.treemap?.sizeTransform;

  const [drilldownStack, setDrilldownStack] = React.useState<DrilldownEntry[]>(
    []
  );

  const initialLevelOffset = isTreemapDrilldownSegment(secondaryDimension)
    ? getTreemapInitialLevelOffset(secondaryDimension, effectiveFilters)
    : 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  React.useEffect(() => {
    setDrilldownStack([]);
  }, [primaryDimension, secondaryDimension, initialLevelOffset]);

  const handleBreadcrumbClick = React.useCallback((targetDepth: number) => {
    setDrilldownStack((prev) => prev.slice(0, targetDepth));
  }, []);

  const children = React.useMemo(() => buildTreemapChildren(data), [data]);
  const asTree: TreeMapData = React.useMemo(
    () => ({ name: 'Root', children }),
    [children]
  );

  if (
    isTreemapDrilldownSegment(secondaryDimension) &&
    primaryDimension !== undefined
  ) {
    const drilldown =
      drilldownStack.length > 0 ? drilldownEntryToFilter(drilldownStack) : null;
    // Crumb i navigates to the view drilled into stack entry i, so its
    // targetDepth (the slice end) is i + 1; the root crumb ("All Roles",
    // "All Geographies", …) resets to the top level for this segment.
    const breadcrumbPath: BreadcrumbSegment[] =
      drilldownStack.length === 0
        ? []
        : [
            { name: ALL_LABEL_BY_SEGMENT[secondaryDimension], targetDepth: 0 },
            ...drilldownStack.map((e, i) => ({
              name: e.name,
              targetDepth: i + 1,
            })),
          ];

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <div
          style={{ flex: 1, minHeight: 0, width: '100%', position: 'relative' }}
        >
          <NestedCompositionTreemapDrilldownProvider
            primaryDimension={primaryDimension}
            segment={secondaryDimension}
            drilldown={drilldown}
          >
            {({
              data: drilldownData,
              monthRefs,
              status,
              parentLookup,
              parentTotals,
              displayLevelId,
              isDisplayLeaf,
              parentLevelId,
            }) => {
              if (status === DataProviderQueryStatus.Fetching) {
                return (
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      minHeight: 0,
                    }}
                  >
                    <ChartLoader />
                  </div>
                );
              }
              const compositionData = drilldownData;
              if (compositionData === undefined || compositionData === null) {
                return null;
              }

              const pushDrill = (
                levelId: string,
                cellId: string,
                cellName: string,
                color?: string,
                parent?: { levelId: string; id: string; name: string }
              ) => {
                if (cellName.toLowerCase() === 'other') return;
                const needsNumericId =
                  secondaryDimension === 'geography' ||
                  secondaryDimension === 'rics';
                const id = needsNumericId ? parseInt(cellId, 10) : cellId;
                if (needsNumericId && !Number.isFinite(id as number)) return;
                const parentId = (() => {
                  if (parent === undefined) return undefined;
                  return needsNumericId ? parseInt(parent.id, 10) : parent.id;
                })();
                setDrilldownStack((prev) => {
                  const top = prev[prev.length - 1];
                  if (
                    top !== undefined &&
                    top.level === levelId &&
                    String(top.id) === String(id)
                  ) {
                    return prev;
                  }
                  const entries: DrilldownEntry[] = [];
                  // Pushing the parent first makes a cell drill equivalent to
                  // header-then-cell, so the breadcrumb reads "Parent > Cell".
                  // Skip it when the parent is already the top entry (e.g. a
                  // click landing on a stale view right after a header drill)
                  // so the stack never holds the same parent twice.
                  const topIsParent =
                    top !== undefined &&
                    parent !== undefined &&
                    top.level === parent.levelId &&
                    String(top.id) === String(parentId);
                  if (
                    !topIsParent &&
                    parent !== undefined &&
                    parentId !== undefined &&
                    (!needsNumericId || Number.isFinite(parentId as number))
                  ) {
                    entries.push({
                      level: parent.levelId,
                      id: parentId,
                      name: parent.name,
                      segment: secondaryDimension,
                      color,
                    });
                  }
                  entries.push({
                    level: levelId,
                    id,
                    name: cellName,
                    segment: secondaryDimension,
                    color,
                  });
                  return [...prev, ...entries];
                });
              };

              const handleCellClick = isDisplayLeaf
                ? undefined
                : (
                    cellId: string,
                    cellName: string,
                    _parentName?: string,
                    color?: string
                  ) => {
                    const parent =
                      parentLevelId === null || parentLookup === null
                        ? undefined
                        : parentLookup.get(nestedTreemapLookupKey(cellId));
                    pushDrill(
                      displayLevelId,
                      cellId,
                      cellName,
                      color,
                      parent === undefined || parentLevelId === null
                        ? undefined
                        : {
                            levelId: parentLevelId,
                            id: parent.parentId,
                            name: parent.parentName,
                          }
                    );
                  };
              const handleHeaderClick =
                parentLevelId === null
                  ? undefined
                  : (groupId: string, groupName: string, color?: string) =>
                      pushDrill(parentLevelId, groupId, groupName, color);

              return (
                <div style={{ width: '100%', height: '100%' }}>
                  <TreemapContent
                    compositionData={compositionData}
                    monthRefs={monthRefs}
                    secondaryDimension={secondaryDimension}
                    drilldown={drilldown}
                    parentLookup={parentLookup}
                    parentTotals={parentTotals}
                    metricToUse={metricToUse}
                    format={format}
                    treemapSizeTransform={treemapSizeTransform}
                    colorPalette={colorPalette}
                    colorOverride={colorOverride}
                    onCellClick={handleCellClick}
                    onHeaderClick={handleHeaderClick}
                    breadcrumbPath={breadcrumbPath}
                    onBreadcrumbClick={handleBreadcrumbClick}
                  />
                </div>
              );
            }}
          </NestedCompositionTreemapDrilldownProvider>
        </div>
      </div>
    );
  }

  return (
    <SharableChart
      Component={ReplotsTreeMap}
      data={asTree}
      format={format}
      sizeTransform={treemapSizeTransform}
      colorPalette={colorPalette}
      colorOverride={colorOverride}
      horizontal
      enclosed
    />
  );
};
